import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CropUploadComponent, FileRequirementsComponent, UploaderState, UploadsApiService } from '@bo/ng-uploads';
import { AsyncPipe, NgClass } from '@angular/common';
import { NgxUploaderModule } from 'ngx-uploader';
import { ImageCropperComponent, ImageCropperModule } from 'ngx-img-cropper';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { AlertModule } from 'ngx-bootstrap/alert';
import { NgbProgressbar, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-crop-upload',
  imports: [
    NgClass,
    NgxUploaderModule,
    AsyncPipe,
    FileRequirementsComponent,
    ImageCropperModule,
    ProgressbarModule,
    AlertModule,
    NgbProgressbar,
    NgbTooltip,
  ],
  templateUrl: './crop-upload.component.html',
  styleUrls: ['../../../../../static/styles/components/file-upload.scss', './crop-upload.component.scss'],
})
export class CropUploadPaysComponent extends CropUploadComponent implements OnChanges {
  override readonly apiService: UploadsApiService;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  @Input() title: string | undefined = 'Drag your file here';
  @Input() text?: string = 'or click the button below to upload your file';
  @Input() textExtra: string = '';
  @Input() fieldId: string | undefined = 'fieldId';
  @ViewChild(ImageCropperComponent) imageCropper?: ImageCropperComponent;

  private currentRotation = 0;
  protected isRotating = false; // Prevent multiple simultaneous rotations
  private originalImageSrc: string | null = null; // Store original image to rotate from

  constructor() {
    const apiService = inject(UploadsApiService);

    super(apiService);

    this.apiService = apiService;
  }

  rotateLeft() {
    if (this.isRotating) return; // Ignore clicks while rotating

    this.currentRotation = (this.currentRotation - 90 + 360) % 360;
    this.rotateImage();
  }

  rotateRight() {
    if (this.isRotating) return; // Ignore clicks while rotating

    this.currentRotation = (this.currentRotation + 90) % 360;
    this.rotateImage();
  }

  private rotateImage() {
    if (!this.canRotate()) {
      return;
    }

    this.isRotating = true;
    this.saveOriginalImageIfNeeded();

    const imageSrc = this.originalImageSrc;
    if (!imageSrc) {
      this.isRotating = false;
      return;
    }

    this.ngZone.runOutsideAngular(() => this.performRotation(imageSrc));
  }

  private canRotate(): boolean {
    return !!(this.image?.src && !this.isRotating);
  }

  private saveOriginalImageIfNeeded(): void {
    if (!this.originalImageSrc && this.image?.src) {
      this.originalImageSrc = this.image.src;
    }
  }

  private performRotation(imageSrc: string): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      this.isRotating = false;
      return;
    }

    const img = new Image();
    img.onload = () => this.handleImageLoad(img, canvas, ctx);
    img.onerror = () => this.handleImageError();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
  }

  private handleImageLoad(img: HTMLImageElement, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
    this.setCanvasDimensions(canvas, img);
    this.drawRotatedImage(ctx, canvas, img);
    const rotatedDataUrl = canvas.toDataURL('image/png');
    this.updateImageWithRotation(rotatedDataUrl);
  }

  private setCanvasDimensions(canvas: HTMLCanvasElement, img: HTMLImageElement): void {
    const shouldSwapDimensions = this.currentRotation === 90 || this.currentRotation === 270;
    canvas.width = shouldSwapDimensions ? img.height : img.width;
    canvas.height = shouldSwapDimensions ? img.width : img.height;
  }

  private drawRotatedImage(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, img: HTMLImageElement): void {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((this.currentRotation * Math.PI) / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    ctx.restore();
  }

  private updateImageWithRotation(rotatedDataUrl: string): void {
    this.ngZone.run(() => {
      this.image.src = rotatedDataUrl;

      if (this.imageCropper) {
        this.imageCropper.setImage(this.image);
      }

      this.cdr.detectChanges();

      setTimeout(() => {
        this.isRotating = false;
      }, 100);
    });
  }

  private handleImageError(): void {
    this.isRotating = false;
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes && (this.state === UploaderState.crop || this.state === UploaderState.selectFile)) {
      this.reset();
      this.error = '';
      this.currentRotation = 0;
      this.originalImageSrc = null; // Reset original image on new upload
    }
    super.ngOnChanges(changes);
  }
}
